import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { AdminAPIResource } from "constants/adminApi";
import { NAME_MATCH_TESTER_PATH } from "constants/routesVariant";
import NameMatchResultView from "components/pages/nameMatchTester/NameMatchResultView";
import { NameMatchEvaluateResponse } from "components/pages/nameMatchTester/types";
import adminApiKeyAxiosInstance from "utils/adminApiKeyAxiosInstance";
import { getUrl } from "utils/getURL";

export interface NameMatchExplanationDialogProps {
  open: boolean;
  onClose: () => void;
  panName?: string | null;
  bankName?: string | null;
  nameMatched?: boolean | null;
  userNameMatchScore?: number | null;
}

function dialogTitle(nameMatched?: boolean | null) {
  if (nameMatched === true) return "Why this name matched";
  if (nameMatched === false) return "Why this name mismatched";
  return "Name match explanation";
}

function NameMatchExplanationDialog({
  open,
  onClose,
  panName,
  bankName,
  nameMatched,
  userNameMatchScore,
}: NameMatchExplanationDialogProps) {
  const navigate = useNavigate();
  const trimmedPan = panName?.trim() || "";
  const trimmedBank = bankName?.trim() || "";
  const namesAvailable = Boolean(trimmedPan && trimmedBank);

  const [result, setResult] = useState<NameMatchEvaluateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const evaluate = useCallback(async () => {
    if (!trimmedPan || !trimmedBank) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await adminApiKeyAxiosInstance.post<NameMatchEvaluateResponse>(
        getUrl(AdminAPIResource.nameMatchEvaluate),
        { panName: trimmedPan, bankName: trimmedBank }
      );
      setResult(response.data);
    } catch (err: unknown) {
      setResult(null);
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message
        || (err as { message?: string })?.message
        || "Failed to evaluate name match";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [trimmedPan, trimmedBank]);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }
    void evaluate();
  }, [open, evaluate]);

  const openInTester = () => {
    const params = new URLSearchParams();
    if (trimmedPan) params.set("panName", trimmedPan);
    if (trimmedBank) params.set("bankName", trimmedBank);
    const query = params.toString();
    navigate(query ? `${NAME_MATCH_TESTER_PATH}?${query}` : NAME_MATCH_TESTER_PATH);
  };

  const footnoteParts: string[] = [];
  if (userNameMatchScore != null) {
    footnoteParts.push(`Bank verification score: ${userNameMatchScore}%`);
  }
  footnoteParts.push("Recomputed live from current PAN and bank names");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="name-match-explanation-title"
    >
      <DialogTitle id="name-match-explanation-title">{dialogTitle(nameMatched)}</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.5 }}
            >
              PAN
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
            >
              {trimmedPan || "—"}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.5 }}
            >
              Bank
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
            >
              {trimmedBank || "—"}
            </Typography>
          </Box>
        </Box>

        {!namesAvailable && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Names unavailable for this customer. PAN and bank account holder names are required
            to explain the match.
          </Alert>
        )}

        {namesAvailable && loading && (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={72} />
            <Skeleton variant="rounded" height={120} />
            <Skeleton variant="rounded" height={160} />
          </Stack>
        )}

        {namesAvailable && error && !loading && (
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void evaluate()}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {namesAvailable && result && !loading && (
          <NameMatchResultView
            result={result}
            prioritizeFailures={nameMatched === false || result.verdict === "REJECT"}
            footnote={footnoteParts.join(" · ")}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600 }}>
          Close
        </Button>
        <Button
          variant="contained"
          endIcon={<OpenInNewIcon />}
          onClick={openInTester}
          disabled={!namesAvailable}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            bgcolor: "#007CBF",
            "&:hover": { bgcolor: "#006aa3" },
          }}
        >
          Open in tester
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NameMatchExplanationDialog;
